import { VoyageImoModel } from '../models/voyage-imo-model'
import { AuthService } from '../services/auth/auth-service'
import { syncHandler } from '../utils/offline-utils'
import { apiCallTracker } from './api-call-tracker';
import { calculateRequestSize, calculateResponseSize } from './api-call-tracker-utils';
import { isOnline } from './helpers';

const notifyNever = (value: never) => {
    console.warn(`Unhandled case: ${value}`)
}

// ETag cache to store ETags and their associated data
const etagCache: Map<string, { etag: string; data: any }> = new Map();

// URLs that support ETag caching
const etagEnabledUrls = new Set([
    'vesselvoyagelegs',
    '/schedulesapi/v1/vesselvoyagelegs',
    '/starconnect-voyage/schedulesapi/v1/vesselvoyagelegs',
    'plans',
    '/voy-vvc-plansapi/v1/plans',
    '/starconnect-voyage/voy-vvc-plansapi/v1/plans',
    'bpr/optimized/portPair',
    '/voy-vvc-plansapi/v1/bpr/optimized/portPair',
    '/starconnect-voyage/voy-vvc-plansapi/v1/bpr/optimized/portPair'
]);

const isVesselOnCloud = (<any>window)['appenv'].vesselOnCloud
const consumerKey = (<any>window)['appenv'].consumerKey
const xRequestor = (<any>window)['appenv'].xRequestor

export async function authFetchGET(
    scope: string | undefined,
    url: string,
    params?: { [index: string]: string },
    headers?: { [index: string]: string },
    includeHeaders?: boolean,
) {
    return _authFetch(scope, url, 'GET', params, headers, includeHeaders)
}

export async function authFetchPOST(
    scope: string | undefined,
    url: string,
    payload: any,
    headers?: { [index: string]: string },
    includeHeaders?: boolean,
) {
    return _authFetch(scope, url, 'POST', payload, headers, includeHeaders)
}

export async function authFetchPUT(
    scope: string | undefined,
    url: string,
    payload: any,
    headers?: { [index: string]: string },
    includeHeaders?: boolean,
) {
    return _authFetch(scope, url, 'PUT', payload, headers, includeHeaders)
}

export async function authFetchDELETE(
    scope: string | undefined,
    url: string,
    headers?: { [index: string]: string },
) {
    return _authFetch(scope, url, 'DELETE', headers)
}

export const TokenExpiredError: string = 'Token expired - needs refresh'

export class FetchStatusCodeError extends Error {
    readonly response: Response

    constructor(response: Response, message: string) {
        super(message)
        this.response = response
    }
}

function serializeHeaders(
    headers: Headers | Record<string, string> | undefined,
): Record<string, string> {
    if (!headers) {
        return {}
    }

    if (headers instanceof Headers) {
        return Object.fromEntries(headers.entries())
    }

    return { ...headers }
}

function createResponseInfo(response: Response | Record<string, any>) {
    return {
        status: response.status ?? null,
        statusText: response.statusText ?? '',
        headers: serializeHeaders(response.headers),
        url: response.url ?? '',
        data: 'data' in response ? response.data ?? null : null,
    }
}

/**
 * Checks if a URL should use ETag caching
 */
function shouldUseETag(method: string, urlString: string): boolean {
    if (method !== 'GET') return false;
    
    // Check against our set of enabled URLs
    if (etagEnabledUrls.has(urlString)) return true;
    
    // Check if the URL ends with any of our enabled paths
    for (const enabledUrl of etagEnabledUrls) {
        if (urlString.endsWith(enabledUrl) || urlString.includes(`/${enabledUrl}`)) {
            return true;
        }
    }
    
    return false;
}

function createCacheKey(url: string, method: string, data?: any): string {
    if (method !== 'GET') {
        return url;
    }
    
    // For GET requests, include only specific query params in the cache key
    const searchParams = new URLSearchParams();
    
    if (data) {
        for (const [key, value] of Object.entries(data)) {
            // Skip timestamp parameter
            if (key === 't') continue;
            
            if (typeof value === 'string') {
                searchParams.set(key, value);
            }
        }
    }
    
    // Add IMO if needed
    if (isVesselOnCloud && VoyageImoModel?.imo) {
        searchParams.set('imo', VoyageImoModel.imo.toString());
    }
    
    const paramString = searchParams.toString();
    const cacheKey = paramString ? `${url.split('?')[0]}?${paramString}` : url.split('?')[0];
    return cacheKey;
}

async function _authFetch(
    scope: string | undefined,
    urlString: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    data?: any,
    headers?: { [key: string]: string },
    includeHeaders?: boolean,
): Promise<any> {
    let url = isVesselOnCloud
        ? (<any>window)['appenv'].apiBasePath + urlString
        : urlString
    url = normalizeCloudImoParams(url)

     // Check if this URL supports ETag caching
     const useETag = shouldUseETag(method, urlString);

     if (AuthService.isRedirecting && isVesselOnCloud) {
        throw new Error('Authentication redirect in progress');
    }

     
     // Create a cache key for this request
    const cacheKey = useETag ? createCacheKey(url, method, data) : '';
    const request: RequestInit = {
        mode: 'cors',
        cache: 'no-cache',
        method: method,
        headers: {
            Accept: 'application/json',
            ...(isVesselOnCloud && { 'consumer-key': consumerKey, 'X-Requestor': xRequestor }),
        },
    }

    if (headers) {
        request.headers = {
            ...request.headers,
            ...headers,
        }
    }

    // being used for error reporting
    const requestInfo = {
        url,
        method,
        data,
        headers: request.headers,
    }

    if (scope && isVesselOnCloud && isOnline()) {
        try {
            const accessToken = await AuthService.getAccessToken(scope)
            // if token refresh failed, immediately stop and redirect
            if (!accessToken) {
                AuthService.startRedirectFlow();
                throw new Error(TokenExpiredError);
            }
            ;(<Record<string, string>>(
                request.headers
            )).Authorization = `Bearer ${accessToken}`
        } catch (error) {
            console.error('Error getting access token:', error)
            if (error instanceof Error && error.message === TokenExpiredError && isVesselOnCloud) {
                AuthService.startRedirectFlow();
            }
            throw error
        }
    }

    // Add If-None-Match header if we have a cached ETag
    if (useETag && etagCache.has(cacheKey)) {
        const cachedETag = etagCache.get(cacheKey)!.etag;
        // Just add the header to the existing request.headers
        (request.headers as Record<string, string>)['If-None-Match'] = cachedETag;
    }
    try {
        const response: Response | undefined = await (() => {
            const args = {
                ...request,
                body: JSON.stringify(data),
                headers: {
                    ...request.headers,
                    'Content-type': 'application/json',
                },
            };
            switch (method) {
                case 'GET':
                    const searchString = (() => {
                        if (!data) return ''

                        const searchParams = new URLSearchParams()

                        for (const [key, value] of Object.entries(data)) {
                            // TODO: What do we do?
                            if (typeof value !== 'string') continue

                            searchParams.set(key, value)
                        }
                        // add imo as url arg - required for cloud
                        addImo(url, searchParams)

                        return `?${searchParams.toString()}`
                    })()
                    // GET requests go directly to fetch() — syncHandler only
                    // adds value for offline-sync-eligible writes (plan PUT/POST)
                    return fetch(`${url}${searchString}`, request);
                case 'POST':
                    return isVesselOnCloud ? syncHandler(`${url}${addImo(url)}`, 'POST', args) : fetch(url, args);
                case 'PUT':
                    return isVesselOnCloud ? syncHandler(`${url}${addImo(url)}`, 'PUT', args) : fetch(url, args);
                case 'DELETE':
                    // DELETE requests go directly to fetch() — no offline sync needed
                    return fetch(`${url}${isVesselOnCloud ? addImo(url) : ''}`, request);
                default:
                    notifyNever(method)
                    throw new Error(`Invalid method: ${method}`)
            }
        })()

        if (!response) {
            const timeoutError = new Error('Timeout')
            ;(timeoutError as any).request = requestInfo
            throw timeoutError
        }

         // Handle 304 Not Modified (ETag match)
         if (response.status === 304 && useETag) {
            console.log(`304 Not Modified received - using cached data for ${cacheKey}`);
            // Return cached data if available
            if (etagCache.has(cacheKey)) {
                const cachedData = etagCache.get(cacheKey)!.data;
                return cachedData;
            } else {
                console.warn(`Got 304 status but no cached data found for ${cacheKey}. Retrying without If-None-Match.`);
                // This shouldn't happen if the server is working correctly
                const newHeaders = { ...headers };
                delete newHeaders['If-None-Match'];
                return _authFetch(scope, urlString, method, data, newHeaders);
            }
        }

        if (response.status === 401) {
            const authError = new Error(
                'Unauthorized - Token expired or invalid',
            )
            ;(authError as any).request = requestInfo
            ;(authError as any).response = createResponseInfo(response)
            throw authError
        }

        if (!response.ok) {
            const statusError = new FetchStatusCodeError(
                response,
                `Error fetching data: ${response.statusText}`,
            )
            ;(statusError as any).request = requestInfo
            throw statusError
        }

       // Process the response data
       let responseData;
       if (response.status === 204 || response.status === 202) {
           responseData = null;
       } else {
           responseData = await response.json();
       }

       if (includeHeaders) {
            const allHeaders: { [key: string]: string } = {}
            response.headers.forEach((value, key) => {
                allHeaders[key] = value
            })

            return {
                data: responseData,
                headers: allHeaders,
            }
        }

       // Store ETag and data for future requests if ETag is present
       if (useETag) {
           // Try getting the ETag header (case-insensitive)
           const newETag = response.headers.get('etag') || response.headers.get('ETag') || response.headers.get('Etag');
           if (newETag) {
               etagCache.set(cacheKey, { etag: newETag, data: responseData });
           }else {
            console.warn(`No ETag header found in response from ${urlString}`);
        }
       }

       // Track bandwidth for RUM metrics when on cloud
       if (isVesselOnCloud) {
           const requestBytes = calculateRequestSize(method, url, data, request.headers);
           const responseBytes = calculateResponseSize(response, responseData);

           apiCallTracker.incrementCallCount(requestBytes, responseBytes, url).catch(err => {
               console.warn('Failed to track API call bandwidth:', err);
           });
       }

       return responseData;
    } catch (error) {
        console.warn(`Error fetching data: ${error}`)
        const errorMessage =
            error instanceof Error ? error.message : 'Unknown error occurred'

        let responseInfo = null

        if (error instanceof FetchStatusCodeError) {
            responseInfo = createResponseInfo(error.response)
        } else if ((error as any).response) {
            responseInfo = createResponseInfo((error as any).response)
        }

        const errorReport: {
            error: string
            message: string
            name: string
            stack: string | undefined
            request: any
            response: ReturnType<typeof createResponseInfo> | null
            originalError: unknown
        } = {
            error: errorMessage,
            message: errorMessage,
            name: error instanceof Error ? error.name : 'Error',
            stack: error instanceof Error ? error.stack : undefined,

            // if it already has response info, preserve it
            request: (error as any).request || requestInfo,
            response: responseInfo,
            originalError: error,
        }

        // trying to get response body if available (may fail for CORS-opaque 5xx responses)
        if (error instanceof FetchStatusCodeError && errorReport.response) {
            try {
                const responseClone = error.response.clone()
                const responseData = await responseClone.json()
                errorReport.response.data = responseData
            } catch (jsonError) {
                try {
                    const responseClone = error.response.clone()
                    const responseText = await responseClone.text()
                    errorReport.response.data = responseText ? (() => {
                        try {
                            return JSON.parse(responseText)
                        } catch {
                            return responseText
                        }
                    })() : null
                } catch (textError) {
                    console.warn('auth-fetch: Could not read error response body', { jsonError, textError })
                    errorReport.response.data = null
                }
            }
        }

        // Top-level request_id so full_error_report (e.g. polar plot download) has it in one place
        const data = errorReport.response?.data
        const headers = errorReport.response?.headers
        const fromBody = data && typeof data === 'object' && (data.request_id ?? data.requestId)
        const fromHeader =
            headers && typeof headers === 'object'
                ? (headers['x-request-id'] ?? headers['X-Request-Id'] ?? null)
                : null
        if (fromBody != null && fromBody !== '') {
            (errorReport as any).request_id = String(fromBody)
        } else if (fromHeader != null && fromHeader !== '') {
            (errorReport as any).request_id = String(fromHeader)
        }

        return errorReport
    }
};

const normalizeCloudImoParams = (url: string): string => {
    if (!isVesselOnCloud || !VoyageImoModel?.imo) {
        return url;
    }

    return url.replace(
        /([?&](?:imo|imo_no|imoNo)=)\d{7}/g,
        `$1${VoyageImoModel.imo}`,
    );
}

const addImo = (url: string, urlSearchParams?: URLSearchParams): string => {
    if (isVesselOnCloud) {
        if (!VoyageImoModel || !VoyageImoModel?.imo || !/^\d{7}$/.test(VoyageImoModel.imo.toString())) {
            throw new Error('Invalid or missing IMO number');
        }
        const searchParams = urlSearchParams || new URLSearchParams();
        if(!/[?&](imo|imo_no|imoNo)=/.test(url)) {
            searchParams.set('imo', VoyageImoModel.imo.toString());
            const specialCharacter = url.includes('?') ? '&' : '?';
            return `${specialCharacter+searchParams.toString()}`;
        }
        return `${searchParams.toString()}`;
    } else {
        return "";
    }
};

export async function authFetchFile(
    scope: string | undefined,
    url: string,
    headers?: { [index: string]: string }
): Promise<Response> {
    const request: RequestInit = {
        mode: 'cors',
        cache: 'no-cache',
        method: 'GET',
        headers: { Accept: 'application/pdf' },
    };

    if (headers) {
        request.headers = { ...request.headers, ...headers };
    }

    if (scope) {
        try {
            const accessToken = await AuthService.getAccessToken(scope)
            if (!accessToken) throw new Error(TokenExpiredError)
            ;(<Record<string, string>>(
                request.headers
            )).Authorization = `Bearer ${accessToken}`
        } catch (error) {
            console.error('Error getting access token:', error);
            throw error;
        }
    }

    try {
        const response = await fetch(url, request);

        if (response.status === 401) {
            throw new Error('Unauthorized - Token expired or invalid');
        }
        if (!response.ok) {
            throw new FetchStatusCodeError(
                response,
                `Error fetching file: ${response.statusText}`
            );
        }

        return response;
    } catch (error) {
        console.error(`Error fetching file: ${error}`);
        throw error;
    }
}
